import GeoJSON, {Geometry, GeometryObject, Position} from 'geojson';
import proj4 from 'proj4';
import * as _ from 'underscore';
import {dataforvalterMapConfig} from './map-config';
import {DisplayFeature, GeometryType} from '../routes/contract-features-3/domain';
import datacatalog from '../../integration/datacatalog/datacatalog-services';
import {LatLngTuple} from 'leaflet';
import WKT from "terraformer-wkt-parser";

export type PositionJson = {
    northing: number,
    easting: number,
    height: number
};

export default class MapUtils {

    // - må konvertere fra UTM33 (EPSG:32633) til WGS84 (EPSG:4326)
    // - proj4() tar inn [x, y] og returnerer [lon, lat]
    static utm33ToWgs84(xy: Position): Position {
        const lonlat = proj4(dataforvalterMapConfig.projections.EPSG32633, dataforvalterMapConfig.projections.EPSG4326, xy);
        return [lonlat[0], lonlat[1]];
    }

    static wgs84ToUtm33(latlng: L.LatLngLiteral): PositionJson {
        const xy = proj4(dataforvalterMapConfig.projections.EPSG4326, dataforvalterMapConfig.projections.EPSG32633, [latlng.lng, latlng.lat]);
        return {
            northing: xy[1],
            easting: xy[0],
            height: 0.0
        };
    }


    static formatPopup(properties: any, indent = 0, footerHtml?: string): string {

        function addLeadingZero(num: number): string {
            return ('0' + num).slice(-2);
        }

        let popupHtml: string = '<div style="margin-left:' + indent + 'mm">' +
            _.map(properties, function(value, key: string) {
                if (key == 'CAPTION') {
                    return '<span style=\'font-weight: bold\'>' + value + '</span>';
                } else if (value instanceof Date) {
                    return key + ': ' + value.getFullYear() + '-' + addLeadingZero(value.getMonth() + 1) + '-' + addLeadingZero(value.getDate());
                } else if (_.isObject(value)) {
                    return key + ':<br/>' + MapUtils.formatPopup(value, indent + 5);
                } else if (_.isNaN(value)) {
                    return key + ': ukjent';
                }
                return key + ': ' + value;
            }).join('<br/>');
        if (footerHtml) {
            popupHtml += '<div class="footer">' + footerHtml + '</div>';
        }
        popupHtml += '</div>';

        return popupHtml;
    }

    static getFeaturePopupProps(feature: DisplayFeature, datacatalog: datacatalog) {
        const typeId = feature.typeId;
        const featureType = datacatalog.getFeatureType(typeId);
        const props: { [key: string]: string } = {
            CAPTION: '<span class=\'color-mid-gray\'>' + typeId + '</span> ' + featureType.name,
            Navn: feature.name
        };

        feature.attributes.forEach(function(attr) {
            const attributeType = featureType.attributeTypes[attr.typeId];
            let value = attr.value;
            if (attributeType.unit) {
                value += ' ' + attributeType.unit;
            }
            props[attributeType.name] = value;
        });

        return props;
    }


    static swap(coordinate: Position): Position {
        return [coordinate[1], coordinate[0]];
    }

    static swapLatLong(geometry: GeoJSON.GeometryObject): GeoJSON.GeometryObject {
        switch (geometry.type) {
            case 'Point': // Single LngLat
                return {type: geometry.type, coordinates: MapUtils.swap(geometry.coordinates)};
            case 'MultiPoint': // Array of LngLat
            case 'LineString': // Array of LngLat
                return {type: geometry.type, coordinates: geometry.coordinates.map(c => MapUtils.swap(c))};
            case 'MultiLineString': // Array of Array of LngLat
            case 'Polygon': // Array of Array of LngLat
                return {type: geometry.type, coordinates: geometry.coordinates.map(coords => coords.map(c => MapUtils.swap(c)))};
            case 'MultiPolygon': // Array of Array of Array of LngLat
                return {type: geometry.type, coordinates: geometry.coordinates.map(mp => mp.map(coords => coords.map(c => MapUtils.swap(c))))};
        }
    }

    static convertUtm33ToWgs84(geometry: GeoJSON.GeometryObject): GeoJSON.GeometryObject {
        switch (geometry.type) {
            case 'Point': // Single LngLat
                return {type: geometry.type, coordinates: MapUtils.utm33ToWgs84(geometry.coordinates) };
            case 'MultiPoint': // Array of LngLat
            case 'LineString': // Array of LngLat
                return {type: geometry.type, coordinates: geometry.coordinates.map(c => MapUtils.utm33ToWgs84(c))};
            case 'MultiLineString': // Array of Array of LngLat
            case 'Polygon': // Array of Array of LngLat
                return {type: geometry.type, coordinates: geometry.coordinates.map(coords => coords.map(c => MapUtils.utm33ToWgs84(c))) };
            case 'MultiPolygon': // Array of Array of Array of LngLat
                return {type: geometry.type, coordinates: geometry.coordinates.map(mp => mp.map(coords => coords.map(c => MapUtils.utm33ToWgs84(c)))) };
        }
    }

    static getGeometryTypeFromWkt(wkt: string): GeometryType {
        const wktCap = wkt.toUpperCase();
        if (wktCap.indexOf('POINT') !== -1 || wktCap.indexOf('MULTIPOINT') !== -1) {
            return 'POINT';
        }
        if (wktCap.indexOf('LINE') !== -1 || wktCap.indexOf('MULTILINE') !== -1) {
            return 'LINE';
        }
        if (wktCap.indexOf('POLYGON') !== -1 || wktCap.indexOf('MULTIPOLYGON') !== -1) {
            return 'POLYGON';
        }
        throw new Error('Unrecognized geometry type');
    }

    static coordinatesToLatLng(coordinates: Position): LatLngTuple {
        return [coordinates[1], coordinates[0]];
    }

    // Returns the first (or last) point in the geometry as latlng
    static geoJsonToLatLng(geoJson: GeometryObject, last?: boolean): LatLngTuple {
        if (geoJson.type == 'Point') {
            return this.coordinatesToLatLng(geoJson.coordinates);
        }
        if (geoJson.type == 'MultiPoint' || geoJson.type == 'LineString') {
            if (last) {
                return this.coordinatesToLatLng(geoJson.coordinates[geoJson.coordinates.length - 1]);
            } else {
                return this.coordinatesToLatLng(geoJson.coordinates[0]);
            }
        }
        if (geoJson.type == 'MultiLineString' || geoJson.type == 'Polygon') {
            if (last) {
                const lastLineString: any = geoJson.coordinates[geoJson.coordinates.length - 1];
                return this.coordinatesToLatLng(lastLineString[lastLineString.length - 1]);
            } else {
                const firstLineString = geoJson.coordinates[0];
                return this.coordinatesToLatLng(firstLineString[0]);
            }
        }
    }

    static reverse(geoJson: Geometry): Geometry {
        if (geoJson.type === 'LineString') {
            const geoJsonCopy = JSON.parse(JSON.stringify(geoJson));
            const coordinates = geoJsonCopy.coordinates;
            coordinates.reverse();
            return geoJsonCopy;
        } else {
            throw new Error('Can only reverse LineString geometries, but was ' + geoJson.type + '.');
        }
    }

    static combinePointsWktToLineWkt(points: string []): string {
        const parsedPoints = points.filter(point => point.includes('POINT')).map(point => {
            return point.substring(point.indexOf('(') + 1, point.indexOf(')'));
        });
        const hasZ = points.every(point => point.includes('Z'));
        return `LINESTRING ${hasZ ? 'Z' : ''}(${parsedPoints.join(', ')})`;
    }

    static getWktFromUnsortedLinestring(wkts: string[]){
        const converted = wkts
            .filter(wkt => MapUtils.getGeometryTypeFromWkt(wkt) === "POINT")
            .map(wkt => WKT.parse(wkt))
            .map(point => MapUtils.convertUtm33ToWgs84(point))
            .map(point => MapUtils.swapLatLong(point))
            .map(point => WKT.convert(point));
        return MapUtils.combinePointsWktToLineWkt(converted);
    }

    static combineLineStringsToOneLineString(linestrings: string[]): string {

        const useHeight = linestrings.every(linestring => linestring.includes('Z'));

        const insideParentheses: string[] = linestrings.map(linestring => {
            return linestring.substring(linestring.indexOf('(') + 1, linestring.indexOf(')'))
        });

        const coordinates: string[] = insideParentheses[0].split(', ');

        // The first coordinate in the second linestring is the same as the last coordinate in the first linestring etc.
        // so we are removing the first coordinate in all linestring except the first
        for (let i = 1; i < insideParentheses.length; i++) {
            insideParentheses[i].split(', ').slice(1).forEach(coordinate => coordinates.push(coordinate));
        }

        if (!useHeight) {
            this.removeHeightCoordinates(coordinates);
        }

        return `LINESTRING ${useHeight ? 'Z' : ''}(${coordinates.join(', ')})`;
    }

    private static removeHeightCoordinates(coordinates: string[]) {
        coordinates.map(coordinate => {
            const xyMaybeZ = coordinate.split(' ');
            if (xyMaybeZ.length === 3) {
                return xyMaybeZ[0] + ' ' + xyMaybeZ[1];
            } else {
                return coordinate;
            }
        })
    }
}

